import { getApps, getApp, initializeApp, FirebaseApp } from '@firebase/app'
import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken,
  isSupported,
  Messaging,
  MessagePayload
} from '@firebase/messaging'
import { getEnvConfig } from 'utils/getEnvConfig'

/**
 * Web FCM wrapper — the "doorbell" transport for realtime sync.
 *
 * The push we receive is a SIGNAL, not data (Android parity): its `data` map
 * carries a `name` like `customer_txns` / `supplier_txns` plus `business_id`,
 * never transaction rows. On receipt the app runs an incremental delta-sync
 * (`refreshMerchantTransactions`), so this module only deals with tokens and
 * message plumbing — it does no data fetching itself.
 */

/** Silent-signal names the backend sends; each maps to a delta re-sync. */
export const PUSH_SIGNAL = {
  CUSTOMER_TXNS: 'customer_txns',
  SUPPLIER_TXNS: 'supplier_txns',
  SUBSCRIPTION_ADDED: 'subscription_added',
  PASSWORD_UPDATED: 'password_updated'
} as const

export interface IPushSignal {
  /** Signal name, e.g. `customer_txns`. */
  name: string
  /** Business the change belongs to, when the payload carries it. */
  businessId?: string
  /** Raw data map, for callers that need extra fields. */
  raw: Record<string, string>
}

const isMissing = (v: unknown) => !v || v === 'undefined' || v === 'null'

const buildFirebaseConfig = (): Record<string, string> | null => {
  const apiKey = getEnvConfig('FIREBASE_API_KEY')
  const appId = getEnvConfig('FIREBASE_APP_ID')
  const projectId = getEnvConfig('FIREBASE_PROJECT_ID')
  const messagingSenderId = getEnvConfig('FIREBASE_MESSAGING_SENDER_ID')
  if (
    isMissing(apiKey) ||
    isMissing(appId) ||
    isMissing(projectId) ||
    isMissing(messagingSenderId)
  ) {
    return null
  }
  return {
    apiKey,
    authDomain: getEnvConfig('FIREBASE_AUTH_DOMAIN'),
    projectId,
    storageBucket: getEnvConfig('FIREBASE_STORAGE_BUCKET'),
    messagingSenderId,
    appId
  }
}

/** Reuse the default app already initialized by FirebaseAnalytics; init if absent. */
const getFirebaseApp = (): FirebaseApp | null => {
  if (getApps().length) {
    return getApp()
  }
  const config = buildFirebaseConfig()
  if (!config) {
    console.warn('[FCM] Skipping init — FIREBASE_* env vars are not set')
    return null
  }
  return initializeApp(config)
}

let messaging: Messaging | null = null
let supportChecked = false
let supported = false

/** True when this browser can receive web push (SW + Push API + Notifications). */
export const isPushSupported = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false
  }
  if (!supportChecked) {
    supportChecked = true
    try {
      supported = await isSupported()
    } catch {
      supported = false
    }
  }
  return supported
}

const getMessagingInstance = async (): Promise<Messaging | null> => {
  if (!(await isPushSupported())) {
    return null
  }
  if (messaging) {
    return messaging
  }
  const app = getFirebaseApp()
  if (!app) {
    return null
  }
  try {
    messaging = getMessaging(app)
    return messaging
  } catch (e) {
    console.warn('[FCM] getMessaging failed', e)
    return null
  }
}

/** Current browser notification permission without prompting. */
export const getPushPermission = (): NotificationPermission => {
  if (typeof Notification === 'undefined') {
    return 'denied'
  }
  return Notification.permission
}

/**
 * Show the native notification permission prompt (no-op if already decided).
 *
 * MUST be called synchronously from within a user-gesture handler (a click). The
 * browser ties the prompt to a fresh user activation: if we instead requested it
 * later — e.g. after awaiting service-worker registration — Chrome silently
 * suppresses the prompt (quiet UI) and Safari rejects the call outright, so the
 * permission is never granted and we re-nudge forever. Returns the resulting
 * permission so callers can decide whether to proceed with token registration.
 */
export const promptForPushPermission = async (): Promise<NotificationPermission> => {
  if (typeof Notification === 'undefined') {
    return 'denied'
  }
  if (Notification.permission !== 'default') {
    return Notification.permission
  }
  try {
    return await Notification.requestPermission()
  } catch {
    return Notification.permission
  }
}

/** True when a Web Push VAPID key is configured; required to mint a web token. */
export const isVapidKeyConfigured = (): boolean => !isMissing(getEnvConfig('FIREBASE_VAPID_KEY'))

/**
 * Prompt for notification permission (if not already decided) and mint a web
 * FCM token bound to the given service-worker registration. Returns null when
 * unsupported, permission is denied, or the VAPID key is missing.
 */
export const requestPushToken = async (
  swRegistration: ServiceWorkerRegistration
): Promise<string | null> => {
  const instance = await getMessagingInstance()
  if (!instance) {
    return null
  }
  const vapidKey = getEnvConfig('FIREBASE_VAPID_KEY')
  if (isMissing(vapidKey)) {
    console.warn('[FCM] Missing FIREBASE_VAPID_KEY — cannot mint a web push token')
    return null
  }
  try {
    const permission =
      getPushPermission() === 'default'
        ? await Notification.requestPermission()
        : getPushPermission()
    if (permission !== 'granted') {
      return null
    }
    const token = await getToken(instance, {
      vapidKey,
      serviceWorkerRegistration: swRegistration
    })
    return token || null
  } catch (e) {
    console.warn('[FCM] requestPushToken failed', e)
    return null
  }
}

/** Normalize a raw FCM payload data map into an IPushSignal. */
export const toPushSignal = (data: Record<string, string> | undefined): IPushSignal | null => {
  if (!data) {
    return null
  }
  const name = data.name || data.type || ''
  if (!name) {
    return null
  }
  return { name, businessId: data.business_id, raw: data }
}

/**
 * Subscribe to FOREGROUND messages (tab focused). Returns an unsubscribe fn.
 * Background messages are handled by the service worker instead.
 */
export const subscribeForegroundMessages = (
  handler: (signal: IPushSignal) => void
): (() => void) => {
  let unsub: (() => void) | null = null
  let cancelled = false
  getMessagingInstance().then((instance) => {
    if (!instance || cancelled) {
      return
    }
    unsub = onMessage(instance, (payload: MessagePayload) => {
      const signal = toPushSignal(payload.data)
      if (signal) {
        handler(signal)
      }
    })
  })
  return () => {
    cancelled = true
    if (unsub) {
      unsub()
    }
  }
}

/** Best-effort token teardown (e.g. on logout). */
export const revokePushToken = async (): Promise<void> => {
  const instance = await getMessagingInstance()
  if (!instance) {
    return
  }
  try {
    await deleteToken(instance)
  } catch (e) {
    console.warn('[FCM] deleteToken failed', e)
  }
}
